import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  LazyMotion,
  domAnimation,
  m,
  AnimatePresence,
  useReducedMotion,
} from "framer-motion";

const QR_SRC = "/images/wecom_QR.png";
const WECOM_ICON_SRC = "/images/wecom.svg";

function useCoarsePointer() {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(hover: none), (pointer: coarse)");
    const sync = () => setCoarse(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return coarse;
}

export default function FloatingContactWidget() {
  const coarsePointer = useCoarsePointer();
  const reduceMotion = useReducedMotion();
  const [hover, setHover] = useState(false);
  const [touchOpen, setTouchOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const showPanel = hover || (coarsePointer && touchOpen);

  const closeTouch = useCallback(() => setTouchOpen(false), []);

  useEffect(() => {
    if (!coarsePointer || !touchOpen) return;
    const onDocPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) closeTouch();
    };
    document.addEventListener("pointerdown", onDocPointer, true);
    return () => document.removeEventListener("pointerdown", onDocPointer, true);
  }, [coarsePointer, touchOpen, closeTouch]);

  const onRootPointerEnter = () => {
    if (!coarsePointer) setHover(true);
  };
  const onRootPointerLeave = () => {
    if (!coarsePointer) setHover(false);
  };

  const onTriggerClick = () => {
    if (coarsePointer) setTouchOpen((v) => !v);
  };

  const playIdleMotion = !reduceMotion && !showPanel;

  return (
    <LazyMotion features={domAnimation}>
      <div
        ref={rootRef}
        className="pointer-events-none fixed right-6 top-1/2 z-[45] -translate-y-1/2 motion-reduce:transition-none"
      >
        <div
          className="group pointer-events-auto flex flex-row-reverse items-center gap-3"
          onPointerEnter={onRootPointerEnter}
          onPointerLeave={onRootPointerLeave}
        >
          <m.button
            type="button"
            aria-expanded={showPanel}
            aria-controls="floating-contact-qr-panel"
            aria-label="联系我们，企业微信"
            className="relative flex h-[52px] w-[52px] shrink-0 items-center justify-center overflow-visible rounded-2xl border border-[#00b7ff]/50 bg-zinc-950 text-white shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_0_0_1px_rgba(0,0,0,0.35),0_4px_14px_rgba(0,0,0,0.18),0_12px_36px_rgba(0,0,0,0.28),0_0_22px_rgba(0,183,255,0.42),0_0_42px_rgba(0,183,255,0.5),0_0_78px_rgba(0,183,255,0.26)] transition-[transform,box-shadow,border-color] duration-300 ease-out hover:border-[#00b7ff]/95 hover:shadow-[0_1px_0_rgba(255,255,255,0.12)_inset,0_0_0_1px_rgba(0,183,255,0.45),0_0_28px_rgba(0,183,255,0.48),0_0_52px_rgba(0,183,255,0.52),0_0_88px_rgba(0,183,255,0.3),0_6px_22px_rgba(0,0,0,0.18),0_18px_48px_rgba(0,183,255,0.48)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00b7ff] focus-visible:ring-offset-2 focus-visible:ring-offset-white motion-reduce:hover:shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_0_0_1px_rgba(0,0,0,0.35),0_4px_14px_rgba(0,0,0,0.18),0_12px_36px_rgba(0,0,0,0.28),0_0_22px_rgba(0,183,255,0.42),0_0_42px_rgba(0,183,255,0.5),0_0_78px_rgba(0,183,255,0.26)]"
            whileHover={
              coarsePointer
                ? undefined
                : { scale: 1.04, transition: { type: "spring", stiffness: 420, damping: 22 } }
            }
            whileTap={{ scale: 0.96 }}
            transition={{ type: "spring", stiffness: 500, damping: 28 }}
            onClick={onTriggerClick}
          >
            {playIdleMotion && (
              <m.span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-2xl border border-[#00b7ff]/90"
                initial={false}
                animate={{ scale: [1, 1.42], opacity: [0.72, 0] }}
                transition={{
                  duration: 2.6,
                  repeat: Infinity,
                  ease: [0.22, 1, 0.36, 1],
                  repeatDelay: 0.35,
                }}
              />
            )}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-[#00b7ff]/28 from-15% via-primary-400/12 to-transparent opacity-0 transition-opacity duration-300 [.group:hover_&]:opacity-100"
            />
            <m.span
              className="relative z-[1] flex items-center justify-center"
              animate={
                playIdleMotion
                  ? {
                      y: [0, -2.5, 0],
                      rotate: [0, -5, 4, 0],
                      filter: [
                        "drop-shadow(0 0 12px rgba(0,183,255,0.68)) drop-shadow(0 0 30px rgba(0,183,255,0.42))",
                        "drop-shadow(0 0 20px rgba(0,183,255,0.95)) drop-shadow(0 0 44px rgba(0,183,255,0.62))",
                        "drop-shadow(0 0 12px rgba(0,183,255,0.68)) drop-shadow(0 0 30px rgba(0,183,255,0.42))",
                      ],
                    }
                  : showPanel
                    ? {
                        y: 0,
                        rotate: 0,
                        filter:
                          "drop-shadow(0 0 10px rgba(0,183,255,0.48)) drop-shadow(0 0 22px rgba(0,183,255,0.28))",
                      }
                    : {
                        y: 0,
                        rotate: 0,
                        filter:
                          "drop-shadow(0 0 12px rgba(0,183,255,0.68)) drop-shadow(0 0 30px rgba(0,183,255,0.42))",
                      }
              }
              transition={
                playIdleMotion
                  ? {
                      duration: 2.8,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }
                  : { duration: 0.2 }
              }
            >
              <img
                src={WECOM_ICON_SRC}
                alt=""
                width={28}
                height={28}
                decoding="async"
                className="h-[26px] w-[26px] object-contain drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]"
              />
            </m.span>
            <span className="sr-only">联系我们</span>
          </m.button>

          <AnimatePresence mode="wait">
            {showPanel && (
              <m.div
                id="floating-contact-qr-panel"
                role="region"
                aria-label="企业微信二维码"
                initial={
                  reduceMotion
                    ? { opacity: 0 }
                    : { opacity: 0, x: 14, scale: 0.96, filter: "blur(4px)" }
                }
                animate={{ opacity: 1, x: 0, scale: 1, filter: "blur(0px)" }}
                exit={
                  reduceMotion
                    ? { opacity: 0 }
                    : { opacity: 0, x: 10, scale: 0.96, filter: "blur(3px)" }
                }
                transition={
                  reduceMotion
                    ? { duration: 0.12 }
                    : {
                        type: "spring",
                        stiffness: 380,
                        damping: 32,
                        mass: 0.85,
                      }
                }
                className="origin-right rounded-2xl border border-default/30 bg-zinc-950/92 p-3 shadow-[0_16px_48px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-xl motion-reduce:animate-none motion-reduce:transition-opacity motion-reduce:duration-150"
              >
                <div className="relative overflow-hidden rounded-xl bg-white p-2 ring-1 ring-black/5">
                  <img
                    src={QR_SRC}
                    alt="企业微信二维码，扫码添加我们"
                    width={168}
                    height={168}
                    className="h-[168px] w-[168px] object-contain motion-reduce:transition-none"
                    decoding="async"
                    loading="lazy"
                  />
                </div>
                <p className="mt-2.5 max-w-[184px] text-center text-[11px] leading-snug tracking-wide text-zinc-100">
                  扫码联系我们
                </p>
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </LazyMotion>
  );
}
